import {
  Button,
  Counter,
  IconButton,
  InfoTooltip,
  Pill,
} from '@wandb/components';
import React, {useMemo, useState} from 'react';
import {twMerge} from 'tailwind-merge';

import {REGEX_METRIC_MAX_LEN} from '../../PanelRunsLinePlot/utils';

/** Show up to this many metrics before collapsing the list */
const METRICS_COLLAPSIBLE_THRESHOLD = 15;
/**
 * Show this many metrics when the list is collapsed.
 *
 * Subtracting from METRICS_COLLAPSIBLE_THRESHOLD ensures there's
 * at least that many more metrics to show. If the difference is
 * too small, the UX would feel unnatural.
 */
const METRICS_COLLAPSED_PAGE_SIZE = METRICS_COLLAPSIBLE_THRESHOLD - 5;

type MultiMetricPanelsListItemProps = {
  regex: string;
  defaultSection: string;
  metrics: string[];
  isAlreadyAdded: boolean;
};

export const MultiMetricPanelsListItem = ({
  regex,
  defaultSection,
  metrics,
  isAlreadyAdded,
}: MultiMetricPanelsListItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const isMetricLimitExceeded = metrics.length > REGEX_METRIC_MAX_LEN;
  const isMetricsCollapsible = metrics.length > METRICS_COLLAPSIBLE_THRESHOLD;
  const [isMetricsCollapsed, setIsMetricsCollapsed] =
    useState(isMetricsCollapsible);

  const displayedMetrics = useMemo(() => {
    if (isMetricsCollapsed) {
      return metrics.slice(0, METRICS_COLLAPSED_PAGE_SIZE);
    }
    if (isMetricLimitExceeded) {
      return metrics.slice(0, REGEX_METRIC_MAX_LEN);
    }
    return metrics;
  }, [isMetricLimitExceeded, isMetricsCollapsed, metrics]);

  const overflowTextClass = isExpanded ? 'break-all' : 'truncate';

  return (
    <div className="rounded-md bg-[light-dark(var(--moon-100),var(--moon-800))] px-16 py-12">
      <div className="flex items-center justify-between gap-12">
        <div className={twMerge('flex-1', overflowTextClass)}>
          <p className={twMerge('leading-5', overflowTextClass)}>{regex}</p>
          <p
            className={twMerge(
              'text-wb-text-tertiary text-sm font-semibold',
              overflowTextClass
            )}>
            {defaultSection}
          </p>
        </div>
        <div className="flex gap-8 items-center flex-shrink-0">
          {isAlreadyAdded && (
            <Pill
              color="green"
              icon="checkmark-alt"
              textValue="Added"
              tooltip={{children: 'This regex has already been added.'}}
            />
          )}
          <Counter
            value={metrics.length}
            label={metrics.length === 1 ? 'metric' : 'metrics'}
            aria-describedby={
              isMetricLimitExceeded
                ? 'metric-limit-exceeded-info-tooltip'
                : undefined
            }
          />
          {isMetricLimitExceeded && (
            <InfoTooltip
              id="metric-limit-exceeded-info-tooltip"
              aria-label="Metric limit exceeded"
              variant="warning"
              size="sm">
              Only the first {REGEX_METRIC_MAX_LEN} matches will be included.
              Consider refining your regex.
            </InfoTooltip>
          )}
          <IconButton
            aria-label={isExpanded ? 'Hide metrics' : 'Show metrics'}
            icon={isExpanded ? 'chevron-up' : 'chevron-down'}
            variant="ghost"
            size="sm"
            onPress={() => {
              setIsExpanded(prev => !prev);
              setIsMetricsCollapsed(isMetricsCollapsible);
            }}
          />
        </div>
      </div>
      {isExpanded && (
        <>
          <ul className="mt-8 list-disc ml-22 break-all">
            {displayedMetrics.map(metric => (
              <li key={metric}>{metric}</li>
            ))}
          </ul>
          {isMetricLimitExceeded && !isMetricsCollapsed && (
            <p className="text-wb-text-tertiary text-sm ml-22">
              ... and {(metrics.length - REGEX_METRIC_MAX_LEN).toLocaleString()}{' '}
              more
            </p>
          )}
          {isMetricsCollapsible && (
            <Button
              variant="ghost"
              size="sm"
              onPress={() => setIsMetricsCollapsed(prev => !prev)}
              ml={6}>
              {isMetricsCollapsed ? 'Show all' : 'Show less'}
            </Button>
          )}
        </>
      )}
    </div>
  );
};
